/**
 * Server-verified analytics identity for a signed-in user.
 *
 * Why this exists: the `vidi_analytics_internal` marker in app_metadata is the
 * only thing that lets the PostHog adapter treat an account as external. Email
 * signups get it from the signup proxy; Google signups get it from the
 * `classify_trusted_google_signup` RPC, which checks a server-written creation
 * receipt. This module only sequences the reads; it trusts nothing the client
 * can assert (no id, email or time is sent to the RPC) and never touches JWT or
 * session app_metadata, which stays stale until a token refresh.
 *
 * Failure of any kind leaves the marker absent => the adapter treats the user as
 * unknown (no analytics). It never blocks cards, login or learning.
 */
export const ANALYTICS_MARKER_KEY = 'vidi_analytics_internal';

export type ServerAuthUser = {
  id: string;
  created_at: string;
  email_confirmed_at?: string | null;
  app_metadata?: Record<string, unknown> | null;
  identities?: Array<{ provider?: string; identity_data?: Record<string, unknown> | null }> | null;
};

type UserReply = { data: { user: ServerAuthUser | null } | null; error: unknown };

export type VerifiedUserDeps = {
  getUser: () => Promise<UserReply>;
  classify: () => Promise<{ data: unknown; error: unknown }>;
  /** False once the session, consent or verification ticket has moved on. */
  isCurrent: () => boolean;
};

function hasMarker(user: ServerAuthUser): boolean {
  const meta = user.app_metadata;
  return (
    !!meta &&
    typeof meta === 'object' &&
    Object.prototype.hasOwnProperty.call(meta, ANALYTICS_MARKER_KEY)
  );
}

/** True only for a confirmed account whose single provider and identity is Google with a JSON-true verified email. */
export function isVerifiedSoleGoogle(user: ServerAuthUser): boolean {
  const meta = user.app_metadata;
  const identities = user.identities;
  if (!meta || !user.email_confirmed_at || meta.provider !== 'google') return false;
  if (
    !Array.isArray(meta.providers) ||
    meta.providers.length !== 1 ||
    meta.providers[0] !== 'google'
  )
    return false;
  if (!Array.isArray(identities) || identities.length !== 1) return false;
  const [identity] = identities;
  return identity?.provider === 'google' && identity.identity_data?.email_verified === true;
}

async function readMatchingUser(
  userId: string,
  deps: VerifiedUserDeps,
): Promise<ServerAuthUser | null> {
  const { data, error } = await deps.getUser();
  return !error && data?.user?.id === userId ? data.user : null;
}

/**
 * One matching server read; when the marker is absent, one classifier attempt and
 * one fresh read. Returns the user whose raw marker the adapter may judge, or null
 * when the identity cannot be confirmed as the current session's.
 */
export async function loadVerifiedAnalyticsUser(
  userId: string,
  deps: VerifiedUserDeps,
): Promise<ServerAuthUser | null> {
  const first = await readMatchingUser(userId, deps);
  if (!first || !deps.isCurrent()) return null;
  if (hasMarker(first)) return first;
  let classified = false;
  try {
    const { data, error } = await deps.classify();
    classified = !error && data === true;
  } catch {
    // Missing function or network failure: stay unknown.
  }
  if (!classified) return first;
  if (!deps.isCurrent()) return null;
  const fresh = await readMatchingUser(userId, deps);
  return fresh && deps.isCurrent() ? fresh : null;
}
